import { navigate } from "@reach/router";
import { getAdBlockById, getCircularMeta, getCircularPagesExtract, getPageBlocks, getSimilarAdBlockProducts, getSimilarAdBlockOffers, getPageIncentive, getCircularIncentive, getAgentProductSuggestions } from "api";
import { PAGE_TYPES } from "config";
import { getIncentiveInfo, parseProduct, parseAgentSuggestionBrands, parseOffer, parseIncentive, sortAdblocksByStatus, updateOffersBasedOnDigitalCoupon, isCouponAutoCheckExcluded } from "utils";

export const createPageSlice = (set, get) => ({
    isLoading: false,
    setIsLoading: (loading) => set({ isLoading: loading }),
    error: "",
    setError: (error) => set({ error }),
    isProductApproved: false,
    setIsProductApproved: (approved) => set({ isProductApproved: approved }),
    // addison-agent image-match status: block product approval while suggestions are still generating
    imageMatchStatus: null,
    imageMatchApplicable: false,
    imageMatchTraceId: null,
    circularName: "",
    circularType: "",
    retailerID: "",
    parentRetailerName: "",
    resetCircularMetaData: () => set({
        circularName: "",
        circularType: "",
        retailerID: "",
        parentRetailerName: "",
    }),
    pageList: [],
    adblockList: [],
    setAdblockList: async (id) => {
        const res = await getPageBlocks(id);
        if (res.success) {
            const adblocksWithUuid = res.payload.adblocks.map(adblock => ({
                ...adblock,
                id: adblock.uuid
            }));
            set({ adblockList: adblocksWithUuid });
        } else {
            console.error('Error fetching adblock list:', res.error);
        }
    },
    currentPageID: "",
    circularUUID: "",
    activePageData: {},
    setActivePageData: async (id) => {
        const page = get().pageList.find(page => page.id === id);
        const res = await getPageBlocks(id);
        if (res.success) {
            const adblocksWithUuid = res.payload.adblocks.map(adblock => ({
                ...adblock,
                id: adblock.uuid
            }));
            const activeBlockUUID = adblocksWithUuid.length > 0 ? adblocksWithUuid[0].id : "";
            set({
                adblockList: adblocksWithUuid,
                currentAdBlockID: activeBlockUUID,
                activePageData: page
            });
            get().setActiveBlockData(activeBlockUUID);
        } else {
            console.error('Error fetching page blocks:', res.error);
        }
        if (page) {
            set({ activePageData: page });
        } else {
            console.warn(`Page with ID ${id} not found.`);
        }
    },
    adblockCoords: { coords_x1: 0, coords_x2: 0, coords_y1: 0, coords_y2: 0 },
    sectionCoords: { coords_x1: 0, coords_x2: 0, coords_y1: 0, coords_y2: 0 },
    currentAdBlockID: "",
    navigateNext: () => {
        // Only use states found in AdBlockStore slices
        get().initProducts && get().initProducts();
        get().initOfferIncentives && get().initOfferIncentives();
        // Reset manual entry if available
        if (get().setIsManualEntry) get().setIsManualEntry(false);
        // Set start time if available
        if (get().setStartTime) get().setStartTime(new Date().toISOString().replace(',', ''));

        const pageList = get().pageList || [];
        const adblockList = get().adblockList || [];
        const currentPageID = get().currentPageID;
        const currentAdBlockID = get().currentAdBlockID;
        const pageIndex = pageList.findIndex((item) => item.id === currentPageID);
        const adblockIndex = adblockList.findIndex((item) => item.id === currentAdBlockID);

        if (adblockIndex < adblockList.length - 1) {
            // Go to next adblock in current page
            get().setCurrentAdBlockID && get().setCurrentAdBlockID(adblockList[adblockIndex + 1].id);
        } else if (
            adblockIndex === adblockList.length - 1 &&
            pageIndex < pageList.length - 1
        ) {
            // Go to next page, reset adblock
            get().setCurrentPageID && get().setCurrentPageID(pageList[pageIndex + 1].id);
            get().setCurrentAdBlockID && get().setCurrentAdBlockID("");
        } else if (
            adblockIndex === adblockList.length - 1 &&
            pageIndex === pageList.length - 1
        ) {
            // End of queue/modal
            // AD-4417: nothing moves off the block here, so refresh it. Otherwise
            // activeBlockData keeps the flags it had before a Complete Match and the
            // is_dupe guards on Approve never engage.
            get().setActiveBlockData(get().currentAdBlockID);
            if (get().endQueueModal && get().endQueueModal.openModal) {
                get().endQueueModal.openModal();
            }
        }
    },
    navigateNextUnapproved: () => {
        // Reset manual entry and set start time
        if (get().setIsManualEntry) get().setIsManualEntry(false);
        if (get().setStartTime) get().setStartTime(new Date().toISOString().replace(',', ''));

        const pageList = get().pageList || [];
        const adblockList = get().adblockList || [];
        const currentPageID = get().currentPageID;
        const currentAdBlockID = get().currentAdBlockID;
        const circularUUID = get().circularUUID;
        const pageIndex = pageList.findIndex((item) => item.id === currentPageID);
        const adblockIndex = adblockList.findIndex((item) => item.id === currentAdBlockID);

        let nextBlock = null;
        
        // Search for next unapproved block in current page
        if (adblockIndex < adblockList.length - 1) {
            nextBlock = adblockIndex + 1;

            // Skip over approved blocks
            while (nextBlock < adblockList.length - 1 && adblockList[nextBlock].offers_approved) {
                nextBlock = nextBlock + 1;
            }

            // Check if we found an unapproved block
            if (!adblockList[nextBlock].offers_approved) {
                const nextAdBlockID = adblockList[nextBlock].id;
                get().setCurrentAdBlockID && get().setCurrentAdBlockID(nextAdBlockID);
                navigate(`/edit/${circularUUID}/${currentPageID}/${nextAdBlockID}`);
            } else {
                nextBlock = null;
            }
        }

        // If no unapproved block found in current page, move to next page or end
        if (!nextBlock) {
            if (
                adblockIndex === adblockList.length - 1 &&
                pageIndex < pageList.length - 1
            ) {
                // Go to next page, reset adblock
                const nextPageID = pageList[pageIndex + 1].id;
                get().setCurrentPageID && get().setCurrentPageID(nextPageID);
                get().setCurrentAdBlockID && get().setCurrentAdBlockID("");
                navigate(`/edit/${circularUUID}/${nextPageID}/0`);
            } else if (
                adblockIndex === adblockList.length - 1 &&
                pageIndex === pageList.length - 1
            ) {
                // End of queue
                // AD-4417: nothing moves off the block here, so refresh it. Otherwise
                // activeBlockData keeps the flags it had before a Complete Match and the
                // is_dupe guards on Approve never engage.
                get().setActiveBlockData(get().currentAdBlockID);
                if (get().endQueueModal && get().endQueueModal.openModal) {
                    get().endQueueModal.openModal();
                }
            }
        }
    },
    activeBlockData: {},
    setActiveBlockData: async (id) => {
        set({ isLoading: true, error: "", toAddBrand: "", leftPanelView: "suggestions", imageMatchStatus: null, imageMatchApplicable: false, imageMatchTraceId: null });
        get().initProducts();
        get().initOfferIncentives();
        if (id) {
            const res = await getAdBlockById(id);
            if (res.success) {
                set({
                    circularUUID: res.payload.circular_uuid,
                    adblockCoords: {
                        coords_x1: res.payload.coords_x1,
                        coords_x2: res.payload.coords_x2,
                        coords_y1: res.payload.coords_y1,
                        coords_y2: res.payload.coords_y2,
                    },
                    sectionCoords: res.payload.section ? {
                        coords_x1: res.payload.section.coords_x1,
                        coords_x2: res.payload.section.coords_x2,
                        coords_y1: res.payload.section.coords_y1,
                        coords_y2: res.payload.section.coords_y2,
                    } : { coords_x1: 0, coords_x2: 0, coords_y1: 0, coords_y2: 0 },
                });
                const meta_data = {
                    newitem_f: res.payload.newitem_f,
                    select_varieties_f: res.payload.select_varieties_f, // TODO: Check if product is adult bev
                    bulkqty_f: res.payload.bulkqty_f,
                    anomaly_f: res.payload.anomaly_f,
                    split: res.payload.split,
                    qr_code_f: res.payload.qr_code_f,
                    coupon_f: res.payload.coupon_f,
                    rebate_f: res.payload.rebate_f,
                    coupon_amnt: res.payload.coupon_amnt,
                    rebate_amnt: res.payload.rebate_amnt,
                    limitedtime_f: (res.payload.group !== null && !res.payload.offers_approved && res.payload.group.limited_time)
                        ? true
                        : res.payload.limitedtime_f,
                    lt_start_dt: (res.payload.group !== null && !res.payload.offers_approved && res.payload.group.limited_time)
                        ? res.payload.group?.start
                        : res.payload.lt_start_dt,
                    lt_end_dt: (res.payload.group !== null && !res.payload.offers_approved && res.payload.group.limited_time)
                        ? res.payload.group?.end
                        : res.payload.lt_end_dt,
                    show_math_f: (res.payload.group !== null && !res.payload.offers_approved)
                        ? res.payload.group?.show_math_f ?? res.payload.show_math_f
                        : res.payload.show_math_f,
                    loyalty_f: (res.payload.group !== null && !res.payload.offers_approved)
                        ? res.payload.group?.loyalty_f ?? res.payload.loyalty_f
                        : res.payload.loyalty_f,
                    meal_deal_f: res.payload.meal_deal_f,
                    online_only_f: (res.payload.group !== null && !res.payload.offers_approved)
                        ? res.payload.group?.online_only_f ?? res.payload.online_only_f
                        : res.payload.online_only_f,
                    sponsored_f: (res.payload.group !== null && !res.payload.offers_approved)
                        ? res.payload.group?.sponsored_f ?? res.payload.sponsored_f
                        : res.payload.sponsored_f,
                    sugar_tax_f: res.payload.sugar_tax_f,
                    brand_equity_f: res.payload.brand_equity_f,
                    // inc_discount_f: res.payload.inc_discount_f,
                    sold_out_f: res.payload.sold_out_f,
                    fsi_retailer_i: res.payload.fsi_retailer_i || '',
                    events: (res.payload.offers_approved && res.payload.event_nm)
                        ? res.payload.event_nm.split(',')
                        : (res.payload.group?.event_text ? res.payload.group.event_text.split(',') : []),
                }
                // Enable Coupon Meta Check if Coupon Page (except excluded retailers, e.g. Target Stores)
                if (!res.payload.offers_approved && get().activePageData?.pageLoc === "4" && !isCouponAutoCheckExcluded(res.payload.retailer_name)) {
                    meta_data.coupon_f = true;
                }
                get().setMetaData(meta_data);
                set({ activeBlockData: res.payload });
                console.log("Active Block Data:", res.payload);
                let product_data = [];
                let offer_data = [];
                let incentive_data = [];
                let updateOfferSggs4DigitalCoupon = false;
                let existingOfferSuggestion = {};
                // product
                if (res.payload.products?.length > 0) {
                    product_data = parseProduct({
                        productData: res.payload.products,
                        sizeData: []
                    });
                }
                const imageMatch = await getAgentProductSuggestions(get().currentAdBlockID);
                set({ imageMatchTraceId: imageMatch.payload?.trace_id ?? null });
                // addison-agent image-match brand suggestions take precedence over openai/existing/fasttext
                if (product_data.length === 0) {
                    set({
                        imageMatchStatus: imageMatch.payload?.status || null,
                        imageMatchApplicable: !!imageMatch.payload?.applicable,
                    });
                    if (imageMatch.success && imageMatch.payload?.status === 'complete' && imageMatch.payload?.brands?.length) {
                        product_data = parseAgentSuggestionBrands(imageMatch.payload.brands);
                        get().setProductSuggestions({ type: 'addison-agent', suggestions: imageMatch.payload.brands, existingSuggestionID: null });
                    }
                }
                if (res.payload?.block_text_suggestion && product_data.length === 0) {
                    product_data = parseProduct({
                        productData: res.payload.block_text_suggestion,
                        sizeData: [],
                        isExisting: false,
                        unique: false,
                        brands: [],
                        open_ai: true,
                        isSuggestion: true
                    });
                    const blockType = res.payload?.block_text?.block_type;
                    let openaiType = 'openai';
                    if (blockType === 'candy') {
                        openaiType = 'openai_candy';
                    } else if (blockType === 'beer') {
                        openaiType = 'openai_beer';
                    } else if (blockType?.includes('laundry')) {
                        openaiType = 'openai_laundry';
                    } else if (blockType === 'beer_gpt') {
                        openaiType='openai_beer_gpt';
                        }
                    get().setProductSuggestions({ type: openaiType, suggestions: res.payload.block_text_suggestion || [], existingSuggestionID: null });
                } else {
                    if (product_data.length === 0) {
                        const getExistingSuggestions = await getSimilarAdBlockProducts(get().currentAdBlockID);
                        if (getExistingSuggestions.success && getExistingSuggestions.payload?.products) {
                            product_data = parseProduct({
                                productData: getExistingSuggestions.payload.products,
                                sizeData: [],
                                isExisting: true,
                                isSuggestion: true
                            });
                            set({ existingProducts: product_data, existingProductInfo: getExistingSuggestions.payload });
                            get().setProductSuggestions({ type: 'existing', suggestions: getExistingSuggestions.payload.products || [], existingSuggestionID: getExistingSuggestions.payload.adblock_id || null });
                        }
                    }
                }
                if (res.payload.suggestions && product_data.length === 0) {
                    if (res.payload.suggestions.products && product_data.length === 0) {
                        product_data = parseProduct({
                            productData: res.payload.suggestions.products,
                            sizeData: [],
                            isExisting: false,
                            unique: true,
                            brands: res.payload.suggestions?.brands,
                            isSuggestion: true
                        });
                        get().setProductSuggestions({ type: 'fasttext', suggestions: res.payload.suggestions.products || [], existingSuggestionID: null });
                    }
                }
                get().addProducts(product_data);
                if (res.payload.suggestions) {
                    get().setCurrentSuggestions(res.payload.suggestions);
                }
                // offers
                if (res.payload.offers.length > 0) {
                    offer_data = parseOffer({
                        offerdata: res.payload.offers, 
                        isExisting: false, 
                        isGroupOffer: false,
                        offersLength: 0
                    })
                } else {
                    const getExistingSuggestions = await getSimilarAdBlockOffers(get().currentAdBlockID);
                    if (getExistingSuggestions.success){
                        existingOfferSuggestion = getExistingSuggestions.payload;
                        if(getExistingSuggestions.payload?.offers) {
                        offer_data = parseOffer({
                            offerdata: getExistingSuggestions.payload.offers,
                            isExisting: true,
                            isGroupOffer: false,
                            offersLength: offer_data.length
                        });
                        set({ existingOffers: offer_data, existingOfferInfo: getExistingSuggestions.payload });
                    }
                }
                }
                if (offer_data.length === 0 && res.payload.suggestions && res.payload.suggestions.offers && !res.payload.offers_approved) {
                    offer_data = parseOffer({
                        offerdata: res.payload.suggestions.offers,
                        isExisting: false,
                        isGroupOffer: false,
                        offersLength: offer_data.length
                    });
                    updateOfferSggs4DigitalCoupon = true;
                }
                // Group Offer
                if (res.payload.group !== null && res.payload.group.offer_type && !res.payload.offers_approved) {
                    const group_offers = res.payload.group.offer_text.split(",")
                    const parsedGroupOffers = parseOffer({
                        offerdata: group_offers,
                        isExisting: false,
                        isGroupOffer: true,
                        offersLength: offer_data.length
                    })
                    offer_data = [...offer_data, ...parsedGroupOffers]
                }

                if (updateOfferSggs4DigitalCoupon && get().activePageData?.digital_coupon_f) {
                    offer_data = updateOffersBasedOnDigitalCoupon(offer_data);
                }
                get().addOffers(offer_data);

                // Group Incentives
                if (res.payload.group !== null && res.payload.group.incentive_type && !res.payload.offers_approved) {
                    const group_incentives = parseIncentive(getIncentiveInfo(res.payload?.group?.incentive_text), 'Adblock', (get().offers.length + get().incentives.length), true);
                    console.log("Group Incentive added:", group_incentives);
                    get().addIncentives(group_incentives);
                }
                if (res.payload.incentive_text) {
                    incentive_data = parseIncentive(getIncentiveInfo(res.payload?.incentive_text), 'Adblock', (get().offers.length + get().incentives.length));
                    console.log("Incentive added:", incentive_data);
                    get().addIncentives(incentive_data);
                }
                // Add incentive suggestions if not approved
                if (!res.payload?.offers_approved && existingOfferSuggestion && existingOfferSuggestion?.incentives) {
                    const existingIncent = existingOfferSuggestion.incentives.toString()
                    if (existingIncent !== "") {
                        incentive_data = parseIncentive(
                            getIncentiveInfo(existingIncent),
                            'Adblock',
                            (get().offers.length + get().incentives.length)
                        );
                        console.log("Incentive suggestion added:", incentive_data);
                        get().addIncentives(incentive_data);
                    }
                }
                // Fetch Page Incentives
                await getPageIncentive(get().currentPageID).then(pageIncentive => {
                    const pageIncentiveText = pageIncentive?.incentive;
                    if (pageIncentiveText) {
                        incentive_data = parseIncentive(getIncentiveInfo(pageIncentiveText), 'Page', (get().offers.length + get().incentives.length));
                        console.log("Page Incentive added:", incentive_data);
                        get().addIncentives(incentive_data);
                    }
                })
                // Fetch Circular Incentives
                await getCircularIncentive(get().circularUUID).then(circularIncentive => {
                    const circularIncentiveText = circularIncentive?.incentive;
                    if (circularIncentiveText) {
                        incentive_data = parseIncentive(getIncentiveInfo(circularIncentiveText), 'Circular', (get().offers.length + get().incentives.length));
                        console.log("Circular Incentive added:", incentive_data);
                        get().addIncentives(incentive_data);
                    }
                })
                get().setCurrentProduct(product_data[0] || null);
                console.log("incentive_data:", incentive_data);
                get().setCurrentOffer(offer_data[0] || incentive_data[0] || null);
                if (offer_data.length > 0) {
                    get().setCurrentItemType("offer");
                } else if (incentive_data.length > 0) {
                    get().setCurrentItemType("incentive");
                } else {
                    get().setCurrentItemType(null);
                }
                // Reconcile the Coupon box (enabled/checked) against loaded offers/incentives
                get().syncCouponFlags(true, meta_data.coupon_f); // prevent coupon check on approved blocks with coupon_f=false, respect saved values on load
            } else {
                console.error('Error fetching adblock data:', res.error);
            }
        } else {
            // AD-4417: an empty id means we are between blocks (navigateNext /
            // navigateNextUnapproved clear it on a page change). Drop the previous
            // block instead of leaving its stale is_dupe / *_approved flags
            // driving the Approve buttons.
            set({ activeBlockData: {} });
        }
        set({ isLoading: false });
    },
    updateActiveBlockData: (updatedData) => {
        set((state) => ({
            activeBlockData: { ...state.activeBlockData, ...updatedData },
        }));
    },
    updateAdblockField: (blockId, field, value) => {
        set((state) => ({
            adblockList: state.adblockList.map(block =>
                block.id === blockId ? { ...block, [field]: value } : block
            ),
        }));
    },
    setCurrentPageID: (id) => {
        get().initProducts();
        get().initOfferIncentives();
        set({ currentPageID: id });
        get().setActivePageData(id);
    },
    pageImg: "",
    imageWidth: 0,
    imageHeight: 0,
    setCurrentAdBlockID: (id) => {
        get().initProducts();
        get().initOfferIncentives();
        set({ currentAdBlockID: id });
        get().setActiveBlockData(id);
    },
    setPageDimensions: (width, height) => set({ pageWidth: width, pageHeight: height }),
    setPageImg: (img) => set({ pageImg: img }),
    setFilename: (name) => set({ filename: name }),
    blockImg: "",
    setBlockImg: (img) => set({ blockImg: img }),
    setImageDimensions: (width, height) => set({ imageWidth: width, imageHeight: height }),
    resetPageData: () => set({
        circularMetaData: {
            circularName: "",
            circularType: "",
            retailerID: "",
            parentRetailerName: "",
        },
        pageList: [],
        adblockList: [],
        currentPageID: "",
        currentAdBlockID: "",
        pageWidth: 0,
        pageHeight: 0,
        pageImg: "",
        filename: "",
        imageWidth: 0,
        imageHeight: 0,
    }),
    initPage: async ({ id, currentPageID, currentAdBlockID }) => {
        set({ isLoading: true, error: "", circularUUID: id });
        try {
            const res = await getCircularMeta(id);
            set({
                circularName: res.payload.name,
                circularType: res.payload.circ_type,
                retailerID: res.payload.retailer_id,
                parentRetailerName: res.payload?.parent_name || "",
            });
            const pageExtractRes = await getCircularPagesExtract(id);
            if (pageExtractRes.success) {
                const pagesData = pageExtractRes.payload.map(page => ({
                    id: page.uuid,
                    crop_approved: page.crop_approved,
                    digital_coupon_f: page.digital_coupon_f,
                    doubletruck_filename: page.doubletruck_filename,
                    filename: page.filename,
                    pageImg: page.image,
                    pageWidth: page.img_width,
                    pageHeight: page.img_height,
                    pageType: PAGE_TYPES.find((item) => item.page_c === page.page_loc)?.page_nm,
                    pageNum: page.page_num,
                    pageLoc: page.page_loc,
                    rejected: page.rejected,
                }))
                // The page param can arrive as "0" (or empty) when a flow such as the
                // End of Queue modal redirects without a specific page. The API expects a
                // valid page UUID, so resolve those placeholders to the first page.
                const resolvedPageID = (currentPageID && currentPageID !== "0")
                    ? currentPageID
                    : (pagesData.length > 0 ? pagesData[0].id : "");
                const active = pagesData.find(page => page.id === resolvedPageID);
                set({
                    pageList: pagesData,
                    currentPageID: resolvedPageID,
                    activePageData: active
                })
                const adblockList = await getPageBlocks(resolvedPageID);
                if (adblockList.success) {
                    if (!adblockList.payload.adblocks) {
                        console.error('No adblocks found for this page.');
                        set({ adblockList: [], currentAdBlockID: "0", setBlockImg: null });
                    } else {
                        const adblocksWithUuid = adblockList.payload.adblocks.map(adblock => ({
                            ...adblock,
                            id: adblock.uuid
                        }));
                        set({ adblockList: adblocksWithUuid });
                        if (currentAdBlockID && currentAdBlockID !== "0") {
                            set({ currentAdBlockID: currentAdBlockID });
                        } else {
                            set({ currentAdBlockID: get().adblockList.length > 0 ? get().adblockList[0].id : "" });
                        }
                        get().setActiveBlockData(get().currentAdBlockID);
                        navigate(`/edit/${id}/${resolvedPageID}/${get().currentAdBlockID}`);
                    }
                } else {
                    console.error('Error fetching adblock list:', adblockList.error);
                }
            }
            set({ isLoading: false });
            setTimeout(() => {
                get().focusFirstField("product-builder");
            }, 100);
        } catch (error) {
            set({ error: error.message, isLoading: false });
        }
    },
    activeField: "",
    setActiveField: (field) => {
        // Also update currentFieldIndex when field is set, so focusNextField works correctly
        // when called from suggestions panel (where activeElement is outside product-builder)
        const focusableElements = document.getElementById("product-builder")?.querySelectorAll(
            'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="combobox"]:not([disabled]), [role="checkbox"]:not([disabled])'
        );
        const elementsArray = Array.from(focusableElements || []);
        const currentIndex = elementsArray.findIndex(el => el === document.activeElement);
        set({ 
            activeField: field,
            currentFieldIndex: currentIndex !== -1 ? currentIndex : get().currentFieldIndex
        });
    },
    currentFieldIndex: -1,
    focusFirstField: (builderId) => {
        const focusableElements = document.getElementById(builderId)?.querySelectorAll(
            'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="combobox"]:not([disabled]), [role="checkbox"]:not([disabled])'
        );
        const elementsArray = Array.from(focusableElements || []);
        if (elementsArray.length > 0) {
            elementsArray[0]?.focus();
            set({ currentFieldIndex: 0 });
        }
    },
    focusPreviousField: (builderId, event) => {
        event.preventDefault();
        const focusableElements = document.getElementById(builderId)?.querySelectorAll(
            'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="combobox"]:not([disabled]), [role="checkbox"]:not([disabled])'
        );
        const elementsArray = Array.from(focusableElements || []);
        if (elementsArray.length === 0) return;

        const currentIndex = elementsArray.findIndex(el => el === document.activeElement);
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : elementsArray.length - 1;
        const prevElement = elementsArray[prevIndex];
        prevElement?.focus();
        set({ currentFieldIndex: prevIndex });
    },

    focusNextField: (builderId, event) => {
        event?.preventDefault?.();
        const focusableElements = document.getElementById(builderId)?.querySelectorAll(
            'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="combobox"]:not([disabled]), [role="checkbox"]:not([disabled])'
        );
        const elementsArray = Array.from(focusableElements || []);
        if (elementsArray.length === 0) return;
        const currentIndex = elementsArray.findIndex(el => el === document.activeElement) !== -1 ? elementsArray.findIndex(el => el === document.activeElement) : get().currentFieldIndex;
        const nextIndex = currentIndex < elementsArray.length - 1 ? currentIndex + 1 : 0;
        const nextElement = elementsArray[nextIndex];
        nextElement?.focus();
        set({ currentFieldIndex: nextIndex });
    },
    focusCurrentField: (builderId) => {
        const focusableElements = document.getElementById(builderId)?.querySelectorAll(
            'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="combobox"]:not([disabled]), [role="checkbox"]:not([disabled])'
        );
        const elementsArray = Array.from(focusableElements || []);
        // Find the actual current element index - don't advance
        const currentIndex = elementsArray.findIndex(el => el === document.activeElement);
        if (currentIndex !== -1) {
            set({ currentFieldIndex: currentIndex });
        }
    },
    isApproving: false,
    isSubmitting: false,
    leftPanelView: "suggestions",
    toAddBrand: "",
});